/**
 * Datos de evolución temporal por caso clínico
 */

import { TimelineCaseConfig } from './types';
import { SNOMED_CT_SYSTEM, LOINC_SYSTEM } from '../types/fhir';

export const TIMELINE_CASES: Record<string, TimelineCaseConfig> = {
  iam: {
    caseId: 'iam',
    steps: [
      {
        id: 'iam-t0',
        label: 'Ingreso',
        offsetMinutes: 0,
        narrative: 'Paciente ingresa con dolor torácico de 2 horas de evolución. Se toman signos vitales y laboratorios iniciales.',
      },
      {
        id: 'iam-t1',
        label: '30 min',
        offsetMinutes: 30,
        narrative: 'Troponina confirmada elevada. ECG muestra elevación del ST en derivaciones anteriores. Se activa código infarto.',
        newNodes: [
          {
            id: 'img-ecg',
            type: 'imaging',
            concept: { code: '11524-6', display: 'ECG: Elevación ST anterior', system: LOINC_SYSTEM },
            system: 'cardiovascular',
            valueString: 'Elevación ST V1-V4 > 2mm',
            severity: 'life-threatening',
            clinicalStatus: 'active',
            physics: { mass: 8, volatility: 0.1, timeCriticality: 1.0 },
            visualization: { baseColor: '#06B6D4', icon: '📊' },
            relationships: [
              { targetId: 'dx-1', type: 'indicates', strength: 0.98, bidirectional: false },
            ],
          },
        ],
        changedNodes: [
          { nodeId: 'vital-1', newValue: 118, trend: 'worsening' },
        ],
        askReclassify: true,
        expectedTriage: 'immediate',
      },
      {
        id: 'iam-t2',
        label: '2 horas',
        offsetMinutes: 120,
        narrative: 'Post-angioplastía primaria exitosa. Stent en arteria descendente anterior. Paciente hemodinámicamente más estable.',
        newNodes: [
          {
            id: 'tx-stent',
            type: 'treatment',
            concept: { code: '232717009', display: 'Angioplastía + Stent', system: SNOMED_CT_SYSTEM },
            system: 'cardiovascular',
            valueString: 'Stent en DA proximal',
            clinicalStatus: 'active',
            physics: { mass: 3, volatility: 0.1, timeCriticality: 0.3 },
            visualization: { baseColor: '#10B981', icon: '💊' },
            relationships: [
              { targetId: 'dx-1', type: 'treats', strength: 0.9, bidirectional: false },
            ],
          },
        ],
        changedNodes: [
          { nodeId: 'vital-1', newValue: 88, trend: 'improving' },
          { nodeId: 'vital-2', newValue: 110, trend: 'improving' },
        ],
        askReclassify: true,
        expectedTriage: 'delayed',
      },
      {
        id: 'iam-t3',
        label: '6 horas',
        offsetMinutes: 360,
        narrative: 'Paciente estable en UCI coronaria. Troponina en descenso. Sin dolor torácico. Se planifica alta a las 48h.',
        changedNodes: [
          { nodeId: 'vital-1', newValue: 76, trend: 'improving' },
          { nodeId: 'vital-2', newValue: 120, trend: 'improving' },
          { nodeId: 'lab-1', newValue: 420, trend: 'improving' },
        ],
        askReclassify: true,
        expectedTriage: 'minor',
      },
    ],
  },

  sepsis: {
    caseId: 'sepsis',
    steps: [
      {
        id: 'sepsis-t0',
        label: 'Ingreso',
        offsetMinutes: 0,
        narrative: 'Paciente ingresa febril y confusa. Se inician hemocultivos y reanimación con fluidos.',
      },
      {
        id: 'sepsis-t1',
        label: '1 hora',
        offsetMinutes: 60,
        narrative: 'Sin respuesta a fluidos. Se inician vasopresores. Antibióticos empíricos administrados.',
        newNodes: [
          {
            id: 'tx-abx',
            type: 'treatment',
            concept: { code: '372840008', display: 'Antibióticos empíricos IV', system: SNOMED_CT_SYSTEM },
            system: 'infectious',
            valueString: 'Meropenem 1g IV + Vancomicina',
            clinicalStatus: 'active',
            physics: { mass: 3, volatility: 0.1, timeCriticality: 0.5, decayHalfLife: 28800 },
            visualization: { baseColor: '#10B981', icon: '💊' },
            relationships: [
              { targetId: 'dx-1', type: 'treats', strength: 0.85, bidirectional: false },
            ],
          },
        ],
        changedNodes: [
          { nodeId: 'vital-1', newValue: 78, trend: 'worsening' },
          { nodeId: 'lab-1', newValue: 5.8, trend: 'worsening' },
        ],
        askReclassify: true,
        expectedTriage: 'immediate',
      },
      {
        id: 'sepsis-t2',
        label: '6 horas',
        offsetMinutes: 360,
        narrative: 'Respuesta parcial a tratamiento. Fiebre en descenso. Lactato mejorando. PA estabilizándose con noradrenalina.',
        changedNodes: [
          { nodeId: 'sintoma-1', newValue: 38.1, trend: 'improving' },
          { nodeId: 'vital-1', newValue: 92, trend: 'improving' },
          { nodeId: 'lab-1', newValue: 3.1, trend: 'improving' },
        ],
        askReclassify: true,
        expectedTriage: 'delayed',
      },
      {
        id: 'sepsis-t3',
        label: '24 horas',
        offsetMinutes: 1440,
        narrative: 'Hemocultivo positivo para E. coli. Antibióticos ajustados. Paciente afebril, sin vasopresores. Mejoría clínica sostenida.',
        changedNodes: [
          { nodeId: 'sintoma-1', newValue: 37.0, trend: 'improving' },
          { nodeId: 'sintoma-2', newValue: false, trend: 'improving' },
          { nodeId: 'vital-1', newValue: 105, trend: 'improving' },
          { nodeId: 'lab-1', newValue: 1.4, trend: 'improving' },
          { nodeId: 'lab-2', newValue: 9800, trend: 'improving' },
        ],
        askReclassify: true,
        expectedTriage: 'minor',
      },
    ],
  },
};
